import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import {
  ArrowRight,
  Building2,
  CalendarRange,
  Check,
  Landmark,
  Loader2,
  Mail,
  ReceiptText,
  TriangleAlert,
  Upload,
  UserPlus,
} from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useToast } from "@/components/ui/use-toast"
import { useAuth, useRbac } from "@/lib/auth"
import { cn } from "@/lib/utils"
import { onWriteError } from "@/lib/writeErrors"

import { fetchRoles, invitar } from "@/routes/accesos/api"
import { crearFacturas } from "@/routes/finance/api"
import { crearBanco, fetchBancos } from "@/routes/finance/datosApi"
import { PersonFormDialog, type PersonFormValues } from "@/routes/masters/PersonFormDialog"
import {
  abrirPeriodos,
  crearSociedad,
  declararRegimen,
  fetchClients,
  fetchCostCenters,
  fetchPaises,
  fetchPeople,
  fetchPeriodos,
  fetchSociedades,
  savePerson,
} from "@/routes/masters/api"
import { CargaEnSitio } from "@/routes/onboarding/CargaEnSitio"
import { FuentesDocumentales } from "@/routes/onboarding/FuentesDocumentales"
import { subirComprobante } from "@/routes/onboarding/api"
import type { Formulario } from "@/routes/onboarding/pasos"
import { t } from "@/lib/i18n"

/**
 * LO QUE SE RELLENA DENTRO DEL PASO.
 *
 * La pantalla anterior sabía exactamente qué faltaba y, para arreglarlo, mandaba
 * a otra pantalla. Eso es media pantalla: quien llega de otra plataforma no
 * conoce el menú, y en el modo guiado —una pregunta a la vez— el enlace te saca
 * del cuestionario en la segunda pregunta y ya no vuelves.
 *
 * ---------------------------------------------------------------------------
 * NO TODOS LOS PASOS TIENEN FORMULARIO, Y ESO ES LA DECISIÓN
 *
 * Hay cinco: sociedad, periodos, origen de documentos, comprobantes y equipo.
 * Los ocho restantes siguen llevando a su pantalla, y no por pereza: su trabajo
 * es REVISAR —una cola de comprobantes, un mapeo de cuentas, un asiento de
 * apertura que tiene que cuadrar—, y eso no cabe en un campo de texto. Meter una
 * cola de 300 comprobantes dentro de una ficha de onboarding la haría inservible
 * en los dos sitios.
 *
 * El criterio, entonces: **hay formulario cuando lo que falta es UN DATO.** Hay
 * enlace cuando lo que falta es un JUICIO.
 *
 * ---------------------------------------------------------------------------
 * CADA FORMULARIO INVALIDA `onboarding-pasos`
 *
 * El estado del paso se DERIVA de los datos (ver `backend/.../pasos.py`), así
 * que escribir el dato no basta: hay que volver a preguntarle al backend. Sin
 * esa invalidación el paso se queda en rojo después de haberlo resuelto, que es
 * la forma más rápida de que alguien lo rellene dos veces.
 */
export function PasoFormulario({ formulario }: { formulario: Formulario }) {
  switch (formulario) {
    case "sociedad":
      return <FormSociedad />
    /*
      UN PASO, TRES PREGUNTAS (2026-10-02, «simplifica el onboarding»): qué
      meses, de dónde, y los archivos. Eran tres fichas seguidas que
      preguntaban por lo mismo desde tres ángulos; van juntas y en ese orden,
      porque el rango de meses decide dónde se asienta lo que llegue después.
    */
    case "comprobantes":
      return (
        <div className="space-y-4">
          <FormPeriodos />
          <FuentesDocumentales embebido />
          <FormComprobantes />
        </div>
      )
    case "equipo":
      return <FormEquipo />
    case "maestros":
      return <FormMaestros />
    case "bancos_fx":
      return <FormBancosFx />
    case "facturacion":
      return <FormFacturacion />
    case "historico":
      return <FormHistorico />
  }
}

/** Envase común: el formulario es la parte operable del paso, no un adorno. */
function Caja({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("rounded-md border bg-surface-sunken/40 px-2.5 py-2.5", className)}>
      {children}
    </div>
  )
}

function useInvalidarPasos() {
  const qc = useQueryClient()
  return React.useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["onboarding-pasos"] })
  }, [qc])
}

function SinPermiso() {
  return (
    <Caja>
      <p className="text-2xs leading-5 text-muted-foreground">
        {t("Tu rol es de solo lectura, así que esto lo tiene que rellenar alguien de tu equipo con permiso de edición. Puedes seguir viendo el resto del traspaso.")}</p>
    </Caja>
  )
}

/* ==========================================================================
   1 · LA SOCIEDAD

   Dos mitades que la pantalla junta y el backend tiene separadas: dar de alta
   la sociedad (POST /masters/sociedades) y declarar su régimen
   (PUT /sociedades/{id}/regimen). Están separadas porque el régimen sale del
   RULEPACK del país, y hasta que el país no está elegido no hay lista que
   ofrecer. Poner un campo libre para el régimen en el alta sería dejar escribir
   «mype» donde el motor espera `MYPE_TRIBUTARIO`: el onboarding se pondría en
   verde y el cálculo seguiría yendo por el general sin que nada avise.
   ========================================================================== */

function FormSociedad() {
  const { can } = useRbac()
  const { toast } = useToast()
  const invalidar = useInvalidarPasos()
  const qc = useQueryClient()

  const sociedades = useQuery({ queryKey: ["sociedades"], queryFn: fetchSociedades })
  const paises = useQuery({ queryKey: ["paises"], queryFn: fetchPaises })

  const [razon, setRazon] = React.useState("")
  const [taxId, setTaxId] = React.useState("")
  const [pais, setPais] = React.useState("")
  const [moneda, setMoneda] = React.useState("")
  const [abierta, setAbierta] = React.useState(false)

  const elegido = (paises.data ?? []).find((p) => p.pais === pais) ?? null

  // La moneda se PROPONE del rulepack y se deja cambiar: una sociedad panameña
  // que lleve sus libros en dólares y una que los lleve en balboas son las dos
  // posibles, y adivinarlo sin dejar corregirlo sería peor que preguntarlo.
  React.useEffect(() => {
    if (elegido?.moneda) setMoneda((m) => m || elegido.moneda || "")
  }, [elegido])

  const crear = useMutation({
    mutationFn: () =>
      crearSociedad({
        razon_social: razon.trim(),
        tax_id: taxId.trim(),
        pais,
        moneda_funcional: moneda.trim().toUpperCase(),
        motivo: "Alta de sociedad desde primeros pasos.",
      }),
    onSuccess: (r) => {
      toast({ title: `${r.razon_social} dada de alta`, description: r.siguiente })
      setRazon("")
      setTaxId("")
      setPais("")
      setMoneda("")
      setAbierta(false)
      void qc.invalidateQueries({ queryKey: ["sociedades"] })
      invalidar()
    },
    onError: onWriteError("la sociedad", "crear"),
  })

  if (!can("edit")) return <SinPermiso />

  const lista = sociedades.data ?? []
  const sinRegimen = lista.filter((s) => !s.regimen_declarado)
  const listoParaCrear =
    razon.trim().length >= 2 && taxId.trim().length >= 3 && pais && moneda.trim().length === 3

  return (
    <Caja className="space-y-2.5">
      {lista.length > 0 ? (
        <ul className="space-y-1.5">
          {lista.map((s) => (
            <li
              key={s.id}
              className="flex flex-wrap items-center gap-2 rounded-md border bg-surface px-2.5 py-1.5"
            >
              <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate text-xs font-medium">{s.razon_social}</span>
              <span className="code text-2xs text-muted-foreground">{s.tax_id}</span>
              <Badge variant="outline">{s.pais}</Badge>
              {s.regimen_declarado ? (
                <Badge variant="success-soft" estado>
                  <Check />
                  {s.regimen_tributario}
                </Badge>
              ) : (
                <Badge variant="warning-soft" estado>
                  <TriangleAlert />
                  {t("sin régimen")}</Badge>
              )}
            </li>
          ))}
        </ul>
      ) : null}

      {/*
        EL RÉGIMEN, UNO POR SOCIEDAD Y DESDE SU RULEPACK. Va después de la lista
        y no dentro de cada fila: son dos preguntas distintas —«¿quién eres?» y
        «¿bajo qué régimen tributas?»— y la segunda solo existe cuando la
        primera está contestada.
      */}
      {sinRegimen.map((s) => (
        <SelectorDeRegimen key={s.id} sociedad={s} onListo={invalidar} />
      ))}

      {abierta || lista.length === 0 ? (
        <div className="space-y-2">
          {lista.length > 0 ? (
            <p className="text-2xs font-medium text-muted-foreground">{t("Otra sociedad")}</p>
          ) : null}
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="soc-razon">{t("Razón social")}</Label>
              <Input
                id="soc-razon"
                value={razon}
                onChange={(e) => setRazon(e.target.value)}
                placeholder={t("SIETE AGENCY S.A.C.")}
                className="text-xs"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="soc-tax">{t("Identificador fiscal")}</Label>
              <Input
                id="soc-tax"
                value={taxId}
                onChange={(e) => setTaxId(e.target.value)}
                placeholder={t("RUC, RFC, CNPJ…")}
                className="code text-xs"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="soc-pais">{t("País")}</Label>
              <Select value={pais} onValueChange={setPais}>
                <SelectTrigger id="soc-pais" className="text-xs">
                  <SelectValue placeholder={paises.isLoading ? "Cargando…" : "Elige el país"} />
                </SelectTrigger>
                <SelectContent>
                  {(paises.data ?? []).map((p) => (
                    <SelectItem key={p.pais} value={p.pais}>
                      {p.pais}
                      {p.madurez ? ` · ${p.madurez.toLowerCase()}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="soc-moneda">{t("Moneda de los libros")}</Label>
              <Input
                id="soc-moneda"
                value={moneda}
                maxLength={3}
                onChange={(e) => setMoneda(e.target.value.toUpperCase())}
                placeholder={t("PEN")}
                className="code text-xs"
              />
            </div>
          </div>

          {/*
            EL PAÍS NO SE PUEDE CAMBIAR DESPUÉS, y decirlo aquí —antes— es la
            diferencia entre una decisión y un 422. Decide el rulepack, el plan
            de cuentas y el calendario, y cambiarlo dejaría los asientos ya
            resueltos contra una normativa que no aplica.
          */}
          <p className="text-2xs leading-5 text-muted-foreground">
            {t("El país decide con qué normativa y qué calendario trabajo, y")}{" "}
            <strong className="font-medium">{t("no lo puedo cambiar después")}</strong>.
            {elegido ? ` En ${elegido.pais} llego hasta: ${elegido.madurez.toLowerCase()}.` : ""}
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              disabled={!listoParaCrear || crear.isPending}
              onClick={() => crear.mutate()}
            >
              {crear.isPending ? <Loader2 className="animate-spin" /> : <Building2 />}
              {crear.isPending ? "Dándola de alta…" : "Dar de alta"}
            </Button>
            {lista.length > 0 ? (
              <Button size="sm" variant="ghost" onClick={() => setAbierta(false)}>
                {t("Cancelar")}</Button>
            ) : null}
          </div>
        </div>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setAbierta(true)}>
          <Building2 />
          {t("Añadir otra sociedad")}</Button>
      )}
    </Caja>
  )
}

function SelectorDeRegimen({
  sociedad,
  onListo,
}: {
  sociedad: { id: string; razon_social: string; pais: string; regimenes_disponibles: {
    codigo: string
    nombre: string | null
    nota: string | null
  }[] }
  onListo: () => void
}) {
  const [codigo, setCodigo] = React.useState("")
  const { toast } = useToast()
  const qc = useQueryClient()

  const declarar = useMutation({
    mutationFn: () =>
      declararRegimen(sociedad.id, {
        regimen_tributario: codigo,
        motivo: "Régimen declarado desde primeros pasos.",
      }),
    onSuccess: (r) => {
      toast({ title: `${sociedad.razon_social}: ${r.regimen_tributario}`, description: r.aviso ?? undefined })
      void qc.invalidateQueries({ queryKey: ["sociedades"] })
      onListo()
    },
    onError: onWriteError("el régimen", "declarar"),
  })

  const elegido = sociedad.regimenes_disponibles.find((r) => r.codigo === codigo) ?? null

  /*
    UNA LISTA VACÍA NO ES «NO HAY RÉGIMEN»: es que el rulepack de ese país
    todavía no los declara. Un desplegable vacío haría pensar lo primero y
    dejaría a alguien buscando una opción que nunca va a aparecer.
  */
  if (sociedad.regimenes_disponibles.length === 0) {
    return (
      <p className="text-2xs leading-5 text-warning">
        <TriangleAlert className="mr-1 inline h-3 w-3" aria-hidden="true" />
        {t("Todavía no tengo la lista de regímenes de")}{" "}{sociedad.pais}{t(", así que el de")}{" "}
        {sociedad.razon_social}{" "}{t("se declara desde Maestros › Sociedades.")}</p>
    )
  }

  return (
    <div className="space-y-1.5 rounded-md border border-warning/40 bg-warning-soft px-2.5 py-2">
      <Label htmlFor={`reg-${sociedad.id}`} className="text-2xs">
        {t("Régimen de")}{" "}{sociedad.razon_social}
      </Label>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={codigo} onValueChange={setCodigo}>
          <SelectTrigger id={`reg-${sociedad.id}`} className="h-7 min-w-[16rem] flex-1 text-xs">
            <SelectValue placeholder={t("Elige el régimen")} />
          </SelectTrigger>
          <SelectContent>
            {sociedad.regimenes_disponibles.map((r) => (
              <SelectItem key={r.codigo} value={r.codigo}>
                {r.nombre || r.codigo}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" disabled={!codigo || declarar.isPending} onClick={() => declarar.mutate()}>
          {declarar.isPending ? "Guardando…" : "Declararlo"}
        </Button>
      </div>
      <p className="text-2xs leading-5 text-muted-foreground">
        {elegido?.nota ??
          "El régimen decide qué impuestos dan crédito. Sin él voy por el general y marco lo dudoso para revisión."}
      </p>
    </div>
  )
}

/* ==========================================================================
   2 · LOS PERÍODOS — un clic

   El rango NO se escribe: lo dicen los propios comprobantes. El backend ya
   calcula `faltantes` (los meses con comprobantes y sin período abierto), así
   que pedir dos fechas sería pedirle a alguien que copie un dato que el sistema
   tiene delante.
   ========================================================================== */

function FormPeriodos() {
  const { activeEntityId } = useAuth()
  const { can } = useRbac()
  const { toast } = useToast()
  const invalidar = useInvalidarPasos()
  const qc = useQueryClient()

  const q = useQuery({
    queryKey: ["periodos", activeEntityId],
    queryFn: () => fetchPeriodos(activeEntityId ?? undefined),
  })
  const sociedades = useQuery({ queryKey: ["sociedades"], queryFn: fetchSociedades })

  const abrir = useMutation({
    mutationFn: (entityId: string) =>
      abrirPeriodos({
        entity_id: entityId,
        solo_faltantes: true,
        motivo: "Meses del histórico abiertos desde primeros pasos.",
      }),
    onSuccess: (r) => {
      toast({
        title: `${r.abiertos} mes(es) abiertos`,
        description: r.nota ?? `${r.ya_existian} ya estaban.`,
      })
      void qc.invalidateQueries({ queryKey: ["periodos"] })
      invalidar()
    },
    onError: onWriteError("los períodos", "abrir"),
  })

  if (!can("edit")) return <SinPermiso />

  const faltantes = q.data?.faltantes ?? []

  if (q.isLoading) {
    return (
      <Caja>
        <p className="text-2xs text-muted-foreground">
          <Loader2 className="mr-1 inline h-3 w-3 animate-spin" />
          {t("Estoy mirando qué meses traen tus comprobantes…")}</p>
      </Caja>
    )
  }

  /*
    SIN FALTANTES NO SE TERMINABA LA PANTALLA, SE TERMINABA EL CAMINO.

    Cesar, punto 14: «el punto 2 "los meses que vas a traer" no hay nada para
    subir información y no queda claro que deben de subir». La causa estaba acá:
    el único camino era «abrir los que tus comprobantes necesitan», y en un
    cliente recién creado NO HAY comprobantes todavía — así que `faltantes` venía
    vacío, la ficha decía «no hay ningún mes por abrir» y no ofrecía nada.

    Y es un círculo, no un detalle: los comprobantes necesitan el período abierto
    para llegar al libro, y el período se deducía de los comprobantes. Quien
    empieza de cero no puede entrar por ningún lado.

    Así que el rango se puede DECLARAR, que es lo que el paso promete en su
    título. El backend ya lo aceptaba (`desde`/`hasta`, AAAA-MM, en
    `AbrirPeriodosIn`); lo que faltaba era pedirlo.
  */
  if (faltantes.length === 0) {
    return <RangoDePeriodos sociedades={sociedades} onListo={invalidar} />
  }

  // Agrupado por sociedad: `abrirPeriodos` toma UNA `entity_id`, y con cuatro
  // sociedades un solo botón «abrir todo» tendría que encadenar cuatro escrituras
  // cuya mitad puede fallar. Un botón por sociedad dice exactamente qué se abrió.
  const porSociedad = new Map<string, { razon: string; meses: string[] }>()
  for (const f of faltantes) {
    const actual = porSociedad.get(f.entity_id) ?? { razon: f.razon_social, meses: [] }
    actual.meses.push(f.periodo)
    porSociedad.set(f.entity_id, actual)
  }

  return (
    <Caja className="space-y-2">
      {[...porSociedad.entries()].map(([entityId, { razon, meses }]) => (
        <div
          key={entityId}
          className="flex flex-wrap items-center gap-2 rounded-md border bg-surface px-2.5 py-1.5"
        >
          <CalendarRange className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium">{razon}</p>
            <p className="text-2xs text-muted-foreground">
              <span className="num-strong">{meses.length}</span> {" "}{t("mes(es) por abrir ·")}{" "}
              <span className="code">{meses[0]}</span>
              {meses.length > 1 ? (
                <>
                  {" "}
                  a <span className="code">{meses[meses.length - 1]}</span>
                </>
              ) : null}
            </p>
          </div>
          <Button
            size="sm"
            disabled={abrir.isPending}
            onClick={() => abrir.mutate(entityId)}
          >
            {abrir.isPending ? <Loader2 className="animate-spin" /> : <CalendarRange />}
            {t("Abrirlos")}</Button>
        </div>
      ))}
    </Caja>
  )
}

/**
 * EL RANGO DECLARADO: desde y hasta qué mes vas a traer.
 *
 * Es la salida del círculo descrito en `FormPeriodos`. Dos campos AAAA-MM y la
 * sociedad, que es lo que `POST /masters/periodos` necesita para abrirlos.
 *
 * SE PROPONE UN RANGO Y NO SE IMPONE. Lo propuesto es «enero de este año hasta
 * el mes pasado», que es lo que trae un traspaso típico a mitad de año: el
 * histórico del ejercicio en curso. Quien traiga dos años lo corrige en dos
 * teclas, y quien no sepa qué poner tiene algo razonable delante en vez de dos
 * campos vacíos — que es la forma más rápida de que nadie rellene ninguno.
 *
 * El tope de períodos lo pone el backend (`MAX_PERIODOS`) y rechaza un rango
 * absurdo con su motivo: un «1990-01 a 2026-12» suele ser una fecha mal
 * tecleada, no una intención.
 */
function RangoDePeriodos({
  sociedades, onListo,
}: {
  sociedades: { data?: { id: string; razon_social: string; pais: string }[] }
  onListo: () => void
}) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const lista = sociedades.data ?? []

  const hoy = new Date()
  const mesPasado = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1)
  const porDefectoHasta = `${mesPasado.getFullYear()}-${String(mesPasado.getMonth() + 1).padStart(2, "0")}`

  const [entityId, setEntityId] = React.useState("")
  const [desde, setDesde] = React.useState(`${mesPasado.getFullYear()}-01`)
  const [hasta, setHasta] = React.useState(porDefectoHasta)

  React.useEffect(() => {
    if (!entityId && lista.length === 1) setEntityId(lista[0].id)
  }, [lista, entityId])

  const abrir = useMutation({
    mutationFn: () =>
      abrirPeriodos({
        entity_id: entityId,
        desde,
        hasta,
        motivo: "Rango del histórico declarado desde primeros pasos.",
      }),
    onSuccess: (r) => {
      toast({
        title: `${r.abiertos} mes(es) abiertos`,
        description: r.nota ?? `${r.ya_existian} ya estaban.`,
      })
      void qc.invalidateQueries({ queryKey: ["periodos"] })
      onListo()
    },
    onError: onWriteError("los períodos", "abrir"),
  })

  const formato = /^\d{4}-(0[1-9]|1[0-2])$/
  const listo =
    !!entityId && formato.test(desde) && formato.test(hasta) && desde <= hasta

  return (
    <Caja className="space-y-2.5">
      <p className="text-2xs leading-5 text-muted-foreground">
        {t("No tengo comprobantes todavía, así que no puedo deducir el rango. Dime desde y hasta qué mes vas a traer y te los abro; cuando lleguen comprobantes de otros meses, los que falten te los ofrezco aquí mismo.")}
      </p>

      <div className="grid gap-2 sm:grid-cols-3 sm:items-end">
        <div className="space-y-1 sm:col-span-3">
          <Label htmlFor="pr-soc">{t("De qué sociedad")}</Label>
          <Select value={entityId} onValueChange={setEntityId}>
            <SelectTrigger id="pr-soc" className="text-xs">
              <SelectValue placeholder={t("Elige la sociedad")} />
            </SelectTrigger>
            <SelectContent>
              {lista.map((x) => (
                <SelectItem key={x.id} value={x.id}>{x.razon_social} · {x.pais}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="pr-desde">{t("Desde")}</Label>
          <Input id="pr-desde" type="month" value={desde}
                 onChange={(e) => setDesde(e.target.value)} className="text-xs" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="pr-hasta">{t("Hasta")}</Label>
          <Input id="pr-hasta" type="month" value={hasta}
                 onChange={(e) => setHasta(e.target.value)} className="text-xs" />
        </div>
        <Button size="sm" disabled={!listo || abrir.isPending} onClick={() => abrir.mutate()}>
          {abrir.isPending ? <Loader2 className="animate-spin" /> : <CalendarRange />}
          {t("Abrirlos")}
        </Button>
      </div>

      {lista.length === 0 ? (
        <p className="text-2xs leading-5 text-warning">
          <TriangleAlert className="mr-1 inline h-3 w-3" aria-hidden="true" />
          {t("Primero hace falta una sociedad: un período es de una sociedad, no del cliente.")}
        </p>
      ) : null}
    </Caja>
  )
}

/* ==========================================================================
   3 · LOS COMPROBANTES — subirlos aquí mismo

   El mismo endpoint que la cola de revisión, con otra lectura del resultado:
   allí interesa el comprobante que acaba de entrar, aquí cuántos entraron y
   cuántos ya estaban. El duplicado NO es un error y se cuenta aparte: es lo
   normal cuando el mismo comprobante ya llegó por el buzón o por Drive.
   ========================================================================== */

function FormComprobantes() {
  const { activeEntityId } = useAuth()
  const { can } = useRbac()
  const { toast } = useToast()
  const invalidar = useInvalidarPasos()
  const entrada = React.useRef<HTMLInputElement>(null)
  const [subiendo, setSubiendo] = React.useState<{ hechos: number; total: number } | null>(null)

  /*
    LA SOCIEDAD SE ELIGE DENTRO DEL PASO, y antes no: el formulario leía
    `activeEntityId` de la barra de arriba y, si no había ninguna elegida,
    mostraba un aviso en ámbar Y NINGÚN BOTÓN. Es la otra mitad del punto 14 de
    Cesar («en el punto 4 igual, "tus comprobantes"»): el paso que promete
    recibir archivos no dejaba elegir ninguno.

    El aviso era correcto —un comprobante es de una sociedad, no de un cliente—
    y la respuesta estaba mal: si hace falta un dato, se pide. Mandar a cambiar
    un selector de otra barra para poder usar esta ficha es exactamente lo que
    el traspaso no tiene que hacer.
  */
  const sociedades = useQuery({ queryKey: ["sociedades"], queryFn: fetchSociedades })
  const [elegida, setElegida] = React.useState("")
  //: Los que se eligieron antes de que hubiera sociedad. Ver `sinSociedades`.
  const [esperando, setEsperando] = React.useState<File[]>([])
  const lista = sociedades.data ?? []
  //: La de la barra manda si hay; si no, la que se elija aquí.
  const destino = activeEntityId ?? (elegida || null)

  React.useEffect(() => {
    if (!activeEntityId && !elegida && lista.length === 1) setElegida(lista[0].id)
  }, [activeEntityId, elegida, lista])

  const subir = async (entrantes: File[] | FileList | null) => {
    const archivos = entrantes ? Array.from(entrantes) : []
    if (!archivos.length) return
    if (!destino) {
      // SE GUARDAN Y SE DICE. Rechazarlos en silencio es lo que hacía que la
      // ficha pareciera rota; subirlos sin sociedad sería peor.
      setEsperando((previos) => [...previos, ...archivos])
      if (entrada.current) entrada.current.value = ""
      return
    }
    const total = archivos.length
    let nuevos = 0
    let duplicados = 0
    let sinInterpretar = 0
    let sinComprobante = 0
    const fallos: string[] = []

    setSubiendo({ hechos: 0, total })
    // De uno en uno y no en paralelo: el backend deduplica por hash dentro de
    // la transacción, y veinte subidas simultáneas del mismo volcado se pisan.
    // Además, un volcado grande en paralelo satura la conexión y el navegador
    // aborta la mitad sin decir cuál.
    for (let i = 0; i < total; i++) {
      const archivo = archivos[i]
      try {
        const r = await subirComprobante(archivo, destino)
        // UN ZIP SON MUCHOS: se cuenta lo de dentro. Antes un ZIP con doce
        // facturas salía como «1 nuevo» —y además no producía ninguna—.
        if (r.lote) {
          nuevos += r.lote.comprobantes
          duplicados += r.lote.duplicados
          sinInterpretar += r.lote.sin_interpretar + r.lote.descartados.length
          sinComprobante += r.lote.sin_comprobante
        } else if (r.duplicado) duplicados++
        else if (r.sin_comprobante) sinComprobante++
        else if (r.normalizado === false) sinInterpretar++
        else nuevos++
      } catch (e) {
        fallos.push(archivo.name)
      }
      setSubiendo({ hechos: i + 1, total })
    }
    setSubiendo(null)
    setEsperando([])
    if (entrada.current) entrada.current.value = ""

    const partes = [`${nuevos} nuevo(s)`]
    if (duplicados) partes.push(`${duplicados} que ya tenía`)
    if (sinComprobante) partes.push(`${sinComprobante} PDF sin su XML (en Revisión)`)
    if (sinInterpretar) partes.push(`${sinInterpretar} guardado(s) sin poder interpretar`)
    if (fallos.length) partes.push(`${fallos.length} que no pude leer`)
    toast({
      variant: fallos.length ? "warning" : undefined,
      title: `Recibí ${total} archivo(s)`,
      description: partes.join(" · "),
    })
    invalidar()
  }

  if (!can("edit")) return <SinPermiso />

  /*
    SIN SOCIEDADES **NO SE PARA**, Y ANTES SÍ.

    Cesar, punto 1 de la cuarta tanda: «en el punto 4 "tus comprobantes" no hay
    nada para cargar los documentos o buscarlos dentro de files». Medido en el
    cliente vacío: cero botones en toda la ficha.

    La causa era una decisión mía de la tanda anterior, y estaba mal. Como
    `subirComprobante` exige `entity_id` y un cliente nuevo no tiene ninguna
    sociedad, el formulario mostraba un aviso en ámbar y se callaba. O sea: el
    paso que promete recibir tus comprobantes no ofrecía dónde ponerlos — que es
    literalmente el defecto que él mismo había señalado en el punto 14.

    Lo que NO se puede hacer es fingir que se suben: un comprobante colgado de
    ninguna sociedad no entra en ningún libro. Así que se hace lo que haría una
    persona: **se reciben los archivos y se quedan esperando**, dicho en voz
    alta, y en cuanto exista la sociedad se suben. El selector de archivos está
    siempre, que es lo que él fue a buscar y no encontró.
  */
  const sinSociedades = lista.length === 0 && !sociedades.isLoading

  return (
    <Caja className="space-y-2">
      {/*
        EL SELECTOR SÓLO APARECE SI NO HAY SOCIEDAD EN LA BARRA. Repetir un mando
        que ya está arriba invita a ponerlos en desacuerdo, y entonces nadie sabe
        a qué sociedad se subió el volcado.
      */}
      {!activeEntityId && !sinSociedades ? (
        <div className="space-y-1">
          <Label htmlFor="cp-soc">{t("A qué sociedad van")}</Label>
          <Select value={elegida} onValueChange={setElegida}>
            <SelectTrigger id="cp-soc" className="text-xs">
              <SelectValue placeholder={t("Elige la sociedad")} />
            </SelectTrigger>
            <SelectContent>
              {lista.map((x) => (
                <SelectItem key={x.id} value={x.id}>{x.razon_social} · {x.pais}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {sinSociedades ? (
        <p className="text-2xs leading-5 text-muted-foreground">
          {t("Todavía no me has dado ninguna sociedad, y un comprobante pertenece a una. Mándamelos igual: te los guardo y los subo en cuanto la des de alta en el paso 1.")}
        </p>
      ) : null}

      {esperando.length > 0 ? (
        <div className="space-y-1.5 rounded-md border border-warning/40 bg-warning-soft px-2.5 py-2">
          <p className="text-2xs leading-5">
            <TriangleAlert className="mr-1 inline h-3 w-3" aria-hidden="true" />
            <strong className="font-medium">{esperando.length}</strong>{" "}
            {t("archivo(s) esperando")}{destino
              ? t(": ya sé de qué sociedad son.")
              : t(" a que me digas de qué sociedad son. Todavía no los he subido.")}
          </p>
          <ul className="space-y-0.5 text-2xs text-muted-foreground">
            {esperando.slice(0, 4).map((f) => <li key={f.name}>{f.name}</li>)}
            {esperando.length > 4 ? (
              <li>{t("y")} {esperando.length - 4} {t("más")}</li>
            ) : null}
          </ul>
          <div className="flex flex-wrap items-center gap-2">
            {destino ? (
              <Button size="sm" disabled={!!subiendo} onClick={() => void subir(esperando)}>
                <Upload />
                {t("Subirlos ahora")}
              </Button>
            ) : null}
            <Button size="xs" variant="ghost" onClick={() => setEsperando([])}>
              {t("Quitarlos")}
            </Button>
          </div>
        </div>
      ) : null}
      <input
        ref={entrada}
        type="file"
        multiple
        accept=".xml,.pdf,.csv,.zip,text/xml,application/xml,application/pdf,text/csv,application/zip"
        className="sr-only"
        onChange={(e) => void subir(e.target.files)}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={!!subiendo}
          onClick={() => entrada.current?.click()}
        >
          {subiendo ? <Loader2 className="animate-spin" /> : <Upload />}
          {subiendo ? `Recibiendo ${subiendo.hechos} de ${subiendo.total}…` : "Elegir archivos"}
        </Button>
        <Button asChild size="sm" variant="ghost">
          <Link to="/contabilidad/revision">
            {t("Ver lo que ya entró")}<ArrowRight />
          </Link>
        </Button>
      </div>
      <p className="text-2xs leading-5 text-muted-foreground">
        {t("XML, PDF, CSV o un ZIP entero. Puedes elegir muchos de una vez. Si alguno ya lo tenía, no lo duplico — te lo digo y sigo.")}</p>
    </Caja>
  )
}

/* ==========================================================================
   4 · EL EQUIPO — invitar sin salir

   Solo se ofrecen los roles que el backend dejaría conceder
   (`concedible_por_ti`). Ofrecer uno que va a rechazar es prometer un acceso
   que no se va a dar.
   ========================================================================== */

/**
 * A QUIÉN INVITAR, dicho por la persona y no por el código del rol.
 *
 * Cesar, punto 18: «tu equipo debería quedar claro a qué usuarios quieres
 * invitar». El desplegable ofrecía `contador`, `cfo`, `gerencia`,
 * `administracion`, `consulta`, `auditor` y `platform_admin` —los códigos
 * crudos—, así que para elegir había que saber ya qué puede hacer cada uno. Y la
 * pregunta que de verdad se hace quien llega no es «¿qué rol le doy?» sino
 * «¿a quién tengo que meter aquí?».
 *
 * Así que cada rol se presenta por QUIÉN ES en una empresa y QUÉ DESBLOQUEA. El
 * orden no es el del backend: primero el contador, porque es el único que puede
 * firmar un cierre y por tanto el único sin el cual hay un muro al final.
 *
 * `platform_admin` NO SE OFRECE AQUÍ aunque el backend lo deje conceder: no es
 * un rol del equipo del cliente, es de quien opera la plataforma. Ofrecerlo
 * junto a «tu contador» invita a dárselo a alguien de la empresa, y es el que
 * atraviesa clientes. Se concede desde Accesos, a conciencia.
 */
const QUIEN_ES: Record<string, { quien: string; porque: string }> = {
  contador: {
    quien: "Tu contador, o quien lleve los libros",
    porque: "Es el único que puede FIRMAR un cierre. Sin al menos uno, el período no se cierra nunca.",
  },
  cfo: {
    quien: "Quien decide con los números",
    porque: "Aprueba y edita, y no firma cierres. Es el que mira margen, caja y runway.",
  },
  gerencia: {
    quien: "Dirección o gerencia general",
    porque: "Ve todo, aprueba y puede dar accesos a los demás.",
  },
  administracion: {
    quien: "Quien carga y ordena el día a día",
    porque: "Sube comprobantes y mantiene maestros. No aprueba ni firma.",
  },
  consulta: {
    quien: "Quien sólo necesita mirar",
    porque: "Lectura y nada más. Para socios o para alguien de otra área.",
  },
  auditor: {
    quien: "Tu auditor externo",
    porque: "Lectura de todo, incluida la pista de auditoría. No puede cambiar nada.",
  },
}

//: No es del equipo del cliente. Ver el docstring de `QUIEN_ES`.
const NO_EN_EL_TRASPASO = ["platform_admin"]

//: El orden en que se ofrecen. El contador primero: es el que desbloquea el cierre.
const ORDEN_ROLES = ["contador", "cfo", "gerencia", "administracion", "consulta", "auditor"]

function FormEquipo() {
  const { can } = useRbac()
  const { toast } = useToast()
  const invalidar = useInvalidarPasos()
  const qc = useQueryClient()
  const [email, setEmail] = React.useState("")
  const [rol, setRol] = React.useState("")

  const roles = useQuery({ queryKey: ["roles"], queryFn: fetchRoles })

  const mandar = useMutation({
    mutationFn: () =>
      invitar({
        email: email.trim(),
        rol,
        motivo: "Invitación enviada desde primeros pasos.",
      }),
    onSuccess: (r) => {
      toast({
        title: `Invité a ${r.email}`,
        description: r.correo_enviado
          ? r.aviso
          : `${r.aviso} Pásale tú el enlace: ${r.ruta_para_aceptar}`,
      })
      setEmail("")
      setRol("")
      void qc.invalidateQueries({ queryKey: ["invitaciones"] })
      void qc.invalidateQueries({ queryKey: ["accesos"] })
      invalidar()
    },
    onError: onWriteError("la invitación", "enviar"),
  })

  if (!can("edit")) return <SinPermiso />

  const concedibles = (roles.data?.roles ?? [])
    .filter((r) => r.concedible_por_ti && !NO_EN_EL_TRASPASO.includes(r.codigo))
    .sort((a, b) => {
      const ia = ORDEN_ROLES.indexOf(a.codigo)
      const ib = ORDEN_ROLES.indexOf(b.codigo)
      // Un rol que no esté en la lista va al final en vez de desaparecer: el día
      // que el backend añada uno, se puede conceder igual.
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
    })
  const elegido = concedibles.find((r) => r.codigo === rol) ?? null
  const valido = /.+@.+\..+/.test(email.trim()) && !!rol
  const hayFirmante = concedibles.some((r) => r.puede_firmar)

  return (
    <Caja className="space-y-2">
      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,11rem)_auto] sm:items-end">
        <div className="space-y-1">
          <Label htmlFor="inv-email">{t("Correo")}</Label>
          <Input
            id="inv-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("contador@tuempresa.com")}
            className="text-xs"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="inv-rol">{t("Con qué alcance")}</Label>
          <Select value={rol} onValueChange={setRol}>
            <SelectTrigger id="inv-rol" className="text-xs">
              <SelectValue placeholder={roles.isLoading ? "Cargando…" : "Elige el rol"} />
            </SelectTrigger>
            <SelectContent>
              {concedibles.map((r) => (
                <SelectItem key={r.codigo} value={r.codigo}>
                  {QUIEN_ES[r.codigo]?.quien ?? r.codigo}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" disabled={!valido || mandar.isPending} onClick={() => mandar.mutate()}>
          {mandar.isPending ? <Loader2 className="animate-spin" /> : <Mail />}
          {mandar.isPending ? "Enviando…" : "Invitar"}
        </Button>
      </div>
      <p className="text-2xs leading-5 text-muted-foreground">
        {elegido
          ? QUIEN_ES[elegido.codigo]?.porque ?? (
            elegido.puede_firmar
              ? "Este rol puede firmar el cierre."
              : "Este rol no firma cierres.")
          : t("Entra con su cuenta de Google y queda con el rol que le des. Lo puedes cambiar después.")}
      </p>

      {/*
        LA LISTA DE A QUIÉN INVITAR, antes de elegir nada. Es la respuesta literal
        al punto 18: quien abre esta ficha no sabe qué roles existen, así que
        decirle «elige el rol» le pide una decisión que todavía no puede tomar.
      */}
      {!elegido ? (
        <ul className="space-y-1 border-t pt-2 text-2xs leading-5">
          <li className="font-medium text-foreground">{t("A quién conviene invitar:")}</li>
          {concedibles.slice(0, 4).map((r) => (
            <li key={r.codigo} className="text-muted-foreground">
              <strong className="font-medium text-foreground">
                {QUIEN_ES[r.codigo]?.quien ?? r.codigo}
              </strong>
              {QUIEN_ES[r.codigo]?.porque ? ` — ${QUIEN_ES[r.codigo].porque}` : ""}
            </li>
          ))}
        </ul>
      ) : null}

      {!hayFirmante && !roles.isLoading ? (
        <p className="text-2xs leading-5 text-warning">
          <TriangleAlert className="mr-1 inline h-3 w-3" aria-hidden="true" />
          {t("Con tu rol no puedes conceder el de contador, que es el único que firma cierres. Pídelo a quien te dio acceso, o el período no se podrá cerrar.")}
        </p>
      ) : null}
      <Button asChild size="xs" variant="ghost" className="-ml-1.5">
        <Link to="/accesos">
          {t("Ver quién tiene acceso hoy")}<ArrowRight />
        </Link>
      </Button>
    </Caja>
  )
}

/* ==========================================================================
   5 · TU GENTE Y TUS CONTRAPARTES — una por una, o en bloque

   Cesar, punto 16: «aparte de cargar la plantilla debería haber un botón de
   agregar persona por persona con los campos en el pop up manual en vez de
   planilla».

   EL DIÁLOGO ES EL MISMO QUE EL DE MAESTROS, importado tal cual. No es ahorro de
   trabajo: es que una persona dada de alta desde el traspaso y otra desde
   Maestros tienen que pasar por las MISMAS validaciones y escribir los MISMOS
   campos. Dos formularios para el mismo maestro divergen en la primera
   migración, y entonces el traspaso crea personas a las que les falta algo que
   el payroll necesita —y eso se descubre al cerrar el mes, no al cargarlas.

   TRES PLANTILLAS Y NO UNA, porque son tres maestros distintos: las personas
   mueven el costo laboral, los clientes el ingreso por cliente, los proveedores
   la memoria de clasificación. Juntarlas en un archivo obligaría a inventar una
   columna «qué soy».
   ========================================================================== */

function FormMaestros() {
  const { can } = useRbac()
  const { toast } = useToast()
  const invalidar = useInvalidarPasos()
  const qc = useQueryClient()
  const [altaPersona, setAltaPersona] = React.useState(false)

  const centros = useQuery({ queryKey: ["cost-centers"], queryFn: fetchCostCenters })
  const clientes = useQuery({ queryKey: ["clients"], queryFn: fetchClients })
  const personas = useQuery({ queryKey: ["people"], queryFn: fetchPeople })

  const guardar = useMutation({
    mutationFn: (values: PersonFormValues) =>
      savePerson({
        data: {
          fullName: values.fullName,
          docNumber: values.docNumber,
          position: values.position,
          costCenterId: values.costCenterId,
          nature: values.nature,
          behavior: values.behavior,
          regime: values.regime,
          billable: values.billable,
          clientIds: values.clientIds,
          startDate: values.startDate,
          salaryBand: values.salaryBand,
          monthlyGross: Number(values.monthlyGross),
          plannedRaise: values.plannedRaise || undefined,
        },
        reason: values.reason,
      }),
    onSuccess: () => {
      toast({
        variant: "success",
        title: t("Persona dada de alta"),
        description: t("Con ella entran su costo laboral y, en Perú, PLAME y AFPnet al mapa de obligaciones."),
      })
      setAltaPersona(false)
      void qc.invalidateQueries({ queryKey: ["people"] })
      invalidar()
    },
    onError: onWriteError("la persona", "dar de alta"),
  })

  if (!can("edit")) return <SinPermiso />

  const cuantas = (personas.data ?? []).length

  return (
    <Caja className="space-y-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => setAltaPersona(true)}>
          <UserPlus />
          {t("Añadir una persona")}
        </Button>
        <span className="text-2xs text-muted-foreground">
          {cuantas === 0
            ? t("Todavía no tengo a nadie.")
            : `${cuantas} ${t("ya cargada(s)")}`}
        </span>
      </div>

      {/*
        EL AVISO DE CENTROS DE COSTO VA ANTES DE ABRIR EL DIÁLOGO. El campo es
        obligatorio ahí dentro, así que sin ninguno cargado el formulario se abre,
        se rellena entero y no se puede guardar — y el motivo aparece recién al
        final, en un desplegable vacío.
      */}
      {(centros.data ?? []).length === 0 && !centros.isLoading ? (
        <p className="text-2xs leading-5 text-warning">
          <TriangleAlert className="mr-1 inline h-3 w-3" aria-hidden="true" />
          {t("No tienes centros de costo todavía, y una persona necesita uno. Se crean en Maestros › Centros de costo; con la plantilla de personas no hacen falta porque los deduzco del archivo.")}
        </p>
      ) : null}

      <div className="space-y-2.5 border-t pt-2.5">
        <CargaEnSitio
          plantilla="personas"
          motivo="Personas cargadas desde primeros pasos."
          invalidar={["people", "payroll"]}
          explicacion={t("O mándame la planilla entera de una vez: nombre, documento, puesto y sueldo. Si tiene más columnas, te digo cuáles no reconocí antes de escribir nada.")}
        />
        <CargaEnSitio
          plantilla="clientes"
          motivo="Clientes cargados desde primeros pasos."
          invalidar={["clients"]}
          explicacion={t("Tus clientes habituales. Sin ellos no hay ingreso por cliente ni concentración: el libro guarda lo que vendiste, no a quién.")}
        />
        <CargaEnSitio
          plantilla="proveedores"
          motivo="Proveedores cargados desde primeros pasos."
          invalidar={["suppliers"]}
          explicacion={t("Tus proveedores. Cada uno que me des es uno que aprendo a clasificar antes: con tres confirmaciones del mismo proveedor ya no te lo pregunto más.")}
        />
      </div>

      <PersonFormDialog
        open={altaPersona}
        onOpenChange={setAltaPersona}
        person={null}
        costCenters={centros.data ?? []}
        clients={clientes.data ?? []}
        pending={guardar.isPending}
        onSubmit={(values) => guardar.mutateAsync(values).then(() => undefined)}
      />
    </Caja>
  )
}

/* ==========================================================================
   6 · BANCOS Y TIPOS DE CAMBIO — sin salir

   Cesar, punto 17: «cuando le das click a cargar bancos y cotizaciones te lleva
   adentro de la plataforma, deberías de poder cargar todo en el onboarding».

   LA CUENTA SE DA DE ALTA SIN SOCIEDAD SI HACE FALTA, y eso es deliberado: en el
   extracto que entrega un banco la cuenta no dice de qué sociedad del grupo es,
   y obligar a elegirla aquí haría adivinar. Asignarla mal pone la caja de una
   sociedad en otra **y el total del grupo sigue cuadrando** —no hay ninguna
   cifra que se descuadre para avisarlo—, así que la asignación es una
   confirmación aparte, en Tesorería, que exige motivo. Ver `datosApi`.

   LAS COTIZACIONES SON EL PASO QUE MÁS SE SUBESTIMA: sin tipos de cambio el
   consolidado no se puede calcular en absoluto. Por eso se dicen las dos cosas
   —qué falta y qué deja de funcionar— y no sólo «carga las cotizaciones».
   ========================================================================== */

function FormBancosFx() {
  const { can } = useRbac()
  const { toast } = useToast()
  const invalidar = useInvalidarPasos()
  const qc = useQueryClient()

  const bancos = useQuery({ queryKey: ["bancos"], queryFn: fetchBancos })
  const sociedades = useQuery({ queryKey: ["sociedades"], queryFn: fetchSociedades })

  const [nombre, setNombre] = React.useState("")
  const [moneda, setMoneda] = React.useState("")
  const [entityId, setEntityId] = React.useState("")
  const [saldo, setSaldo] = React.useState("")

  const crear = useMutation({
    mutationFn: () =>
      crearBanco({
        nombre: nombre.trim(),
        moneda: moneda.trim().toUpperCase(),
        entity_id: entityId || null,
        saldo_final: saldo === "" ? null : Number(saldo),
        motivo: "Cuenta bancaria dada de alta desde primeros pasos.",
      }),
    onSuccess: (r) => {
      toast({ title: `${r.nombre} dada de alta`, description: r.aviso ?? undefined })
      setNombre(""); setMoneda(""); setEntityId(""); setSaldo("")
      void qc.invalidateQueries({ queryKey: ["bancos"] })
      invalidar()
    },
    onError: onWriteError("la cuenta bancaria", "crear"),
  })

  if (!can("edit")) return <SinPermiso />

  const lista = bancos.data ?? []
  const listo = nombre.trim().length >= 2 && moneda.trim().length === 3

  return (
    <Caja className="space-y-2.5">
      {lista.length > 0 ? (
        <ul className="space-y-1.5">
          {lista.slice(0, 6).map((c) => (
            <li key={c.id}
                className="flex flex-wrap items-center gap-2 rounded-md border bg-surface px-2.5 py-1.5">
              <Landmark className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate text-xs font-medium">{c.nombre}</span>
              <Badge variant="outline" className="code">{c.moneda ?? "—"}</Badge>
              {c.razon_social ? (
                <Badge variant="success-soft" estado><Check />{c.pais}</Badge>
              ) : (
                <Badge variant="warning-soft" estado>
                  <TriangleAlert />{t("sin sociedad")}
                </Badge>
              )}
            </li>
          ))}
          {lista.length > 6 ? (
            <li className="text-2xs text-muted-foreground">
              {t("y")} {lista.length - 6} {t("más")}
            </li>
          ) : null}
        </ul>
      ) : null}

      <div className="grid gap-2 sm:grid-cols-2">
        <div className="space-y-1 sm:col-span-2">
          <Label htmlFor="bk-nom">{t("Nombre de la cuenta")}</Label>
          <Input id="bk-nom" value={nombre} onChange={(e) => setNombre(e.target.value)}
                 placeholder={t("BCP corriente soles")} className="text-xs" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="bk-mon">{t("Moneda")}</Label>
          <Input id="bk-mon" value={moneda} maxLength={3}
                 onChange={(e) => setMoneda(e.target.value.toUpperCase())}
                 placeholder="PEN" className="code text-xs" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="bk-saldo">{t("Saldo de hoy")}</Label>
          <Input id="bk-saldo" value={saldo} inputMode="decimal"
                 onChange={(e) => setSaldo(e.target.value)} className="num text-xs" />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label htmlFor="bk-soc">{t("De qué sociedad es")}</Label>
          <Select value={entityId} onValueChange={setEntityId}>
            <SelectTrigger id="bk-soc" className="text-xs">
              <SelectValue placeholder={t("Si no lo sabes, déjalo y lo asignas después")} />
            </SelectTrigger>
            <SelectContent>
              {(sociedades.data ?? []).map((x) => (
                <SelectItem key={x.id} value={x.id}>{x.razon_social} · {x.pais}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/*
        QUE SE PUEDEN AÑADIR VARIAS, DICHO. Cesar, punto 3 de la cuarta tanda:
        «¿puedes añadir más de una cuenta en el punto 6?». La respuesta era que
        sí —el formulario se vacía al guardar y la lista de arriba crece— y es
        exactamente el mismo caso que las carpetas de Drive en el punto 15 de la
        tanda anterior: la capacidad estaba y la pantalla no la decía.

        Así que el botón cuenta cuántas lleva y el texto lo dice. No hace falta
        otro mando: lo que faltaba era la frase.
      */}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={!listo || crear.isPending} onClick={() => crear.mutate()}>
          {crear.isPending ? <Loader2 className="animate-spin" /> : <Landmark />}
          {crear.isPending
            ? t("Dándola de alta…")
            : lista.length > 0 ? t("Añadir otra cuenta") : t("Añadir la cuenta")}
        </Button>
        <span className="text-2xs text-muted-foreground">
          {lista.length > 0
            ? `${lista.length} ${t("ya dadas de alta · puedes añadir todas las que tengas, una a una")}`
            : t("Puedes añadir todas las que tengas, una a una.")}
        </span>
      </div>

      <div className="space-y-2.5 border-t pt-2.5">
        <CargaEnSitio
          plantilla="bancos"
          motivo="Cuentas bancarias cargadas desde primeros pasos."
          invalidar={["bancos", "tesoreria"]}
          explicacion={t("O todas de una vez por archivo, con su saldo.")}
        />
        <CargaEnSitio
          plantilla="tipos-cambio"
          motivo="Cotizaciones cargadas desde primeros pasos."
          invalidar={["tipos-cambio"]}
          explicacion={
            <>
              {t("Las cotizaciones de tus monedas, pegadas desde tu hoja. Son unidades por UN dólar: la fila PEN = 3,752 son soles por dólar.")}{" "}
              <strong className="font-medium">
                {t("Sin esto no puedo calcular el consolidado en absoluto.")}
              </strong>
            </>
          }
        />
      </div>
    </Caja>
  )
}

/* ==========================================================================
   7 · LO QUE FACTURASTE — manual, en bloque o con plantilla

   Cesar, punto 19: «cargar facturación igual deberías de poder cargarlo ahí
   mismo: manual por cliente, o en bulk, o con alguna plantilla». Las tres vías,
   y son literalmente las tres que pidió.

   OJO CON QUÉ ES ESTO Y QUÉ NO: es la facturación COMERCIAL —tu hoja, lo que tú
   crees que facturaste— y no el comprobante fiscal, que entra por el paso de
   comprobantes. Tener las dos y poder compararlas es todo el punto, así que
   confundirlas aquí destruiría la comparación: se dice en el texto.
   ========================================================================== */

function FormFacturacion() {
  const { can } = useRbac()
  const { toast } = useToast()
  const invalidar = useInvalidarPasos()
  const qc = useQueryClient()

  const [cliente, setCliente] = React.useState("")
  const [numero, setNumero] = React.useState("")
  const [fecha, setFecha] = React.useState("")
  const [moneda, setMoneda] = React.useState("USD")
  const [importe, setImporte] = React.useState("")
  const [pais, setPais] = React.useState("")

  const sociedades = useQuery({ queryKey: ["sociedades"], queryFn: fetchSociedades })
  const paises = React.useMemo(
    () => [...new Set((sociedades.data ?? []).map((x) => x.pais))].sort(),
    [sociedades.data],
  )

  const crear = useMutation({
    mutationFn: () =>
      crearFacturas([{
        pais,
        cliente: cliente.trim(),
        numero: numero.trim(),
        fecha_emision: fecha,
        moneda: moneda.trim().toUpperCase() || null,
        // EN DÓLARES, y el campo lo dice. Ver `crearFacturas`: el modelo
        // consolida al dólar y convertir aquí dejaría el tipo de cambio en el
        // navegador, donde nadie lo puede auditar.
        importe_usd: Number(importe),
      }], "Factura cargada desde primeros pasos."),
    onSuccess: () => {
      toast({
        title: t("Factura cargada"),
        description: t("Entra en tu cartera y en el ingreso por cliente."),
      })
      setCliente(""); setNumero(""); setImporte("")
      void qc.invalidateQueries({ queryKey: ["facturas"] })
      void qc.invalidateQueries({ queryKey: ["cobranza-cartera"] })
      invalidar()
    },
    onError: onWriteError("la factura", "cargar"),
  })

  if (!can("edit")) return <SinPermiso />

  const listo =
    !!pais && cliente.trim().length >= 2 && numero.trim().length >= 1
    && !!fecha && Number(importe) > 0

  return (
    <Caja className="space-y-2.5">
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="fa-cli">{t("Cliente")}</Label>
          <Input id="fa-cli" value={cliente} onChange={(e) => setCliente(e.target.value)}
                 className="text-xs" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="fa-num">{t("Número")}</Label>
          <Input id="fa-num" value={numero} onChange={(e) => setNumero(e.target.value)}
                 placeholder="F001-00123" className="code text-xs" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="fa-fec">{t("Fecha de emisión")}</Label>
          <Input id="fa-fec" type="date" value={fecha}
                 onChange={(e) => setFecha(e.target.value)} className="text-xs" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="fa-pais">{t("De qué país")}</Label>
          <Select value={pais} onValueChange={setPais}>
            <SelectTrigger id="fa-pais" className="text-xs">
              <SelectValue placeholder={t("Elige el país")} />
            </SelectTrigger>
            <SelectContent>
              {paises.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="fa-imp">{t("Importe total en USD")}</Label>
          <Input id="fa-imp" value={importe} inputMode="decimal"
                 onChange={(e) => setImporte(e.target.value)} className="num text-xs" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="fa-mon">{t("Moneda en que la emitiste")}</Label>
          <Input id="fa-mon" value={moneda} maxLength={3}
                 onChange={(e) => setMoneda(e.target.value.toUpperCase())}
                 className="code text-xs" />
        </div>
      </div>

      {/*
        LOS DOS CAMPOS DE MONEDA NO SON UNA REDUNDANCIA, y decirlo evita que
        alguien ponga el importe local en el campo de dólares. El modelo
        financiero consolida al dólar y la moneda de emisión es un dato del
        documento: son dos cosas distintas y las dos hacen falta.
      */}
      <p className="text-2xs leading-5 text-muted-foreground">
        {t("El importe va en dólares porque todo el modelo financiero consolida al dólar; la moneda de al lado es la del documento. Si la emitiste en soles, convierte el importe con el tipo de cambio de la fecha y pon PEN al lado.")}
      </p>

      <Button size="sm" disabled={!listo || crear.isPending} onClick={() => crear.mutate()}>
        {crear.isPending ? <Loader2 className="animate-spin" /> : <ReceiptText />}
        {crear.isPending ? t("Cargándola…") : t("Añadir la factura")}
      </Button>

      <div className="border-t pt-2.5">
        <CargaEnSitio
          plantilla="ventas"
          motivo="Facturación cargada desde primeros pasos."
          invalidar={["facturas", "cobranza-cartera", "finance-kpis"]}
          explicacion={
            <>
              {t("O la hoja entera, como esté.")}{" "}
              <strong className="font-medium">
                {t("Esto es tu facturación comercial, no el comprobante fiscal:")}
              </strong>{" "}
              {t("el comprobante entra por el paso de comprobantes. Tener las dos es lo que me deja decirte en qué se diferencian, y esa diferencia casi siempre es un hallazgo.")}
            </>
          }
        />
      </div>
    </Caja>
  )
}

/* ==========================================================================
   8 · EL HISTÓRICO DEL SISTEMA ANTERIOR

   Cesar, punto 20: «los últimos 3 puntos igual debería de botártelo todo en el
   onboarding». De esos cuatro pasos, éste es el que de verdad tiene un archivo
   que dar, y hasta ahora sólo decía «ir al diario».

   TRES LECTORES PORQUE SON TRES SISTEMAS, y cada uno exporta otra cosa: CONCAR
   una fila por LÍNEA de asiento, CONTPAQi una por MOVIMIENTO de póliza, Odoo sus
   apuntes con debe y haber. Ofrecer «sube tu histórico» sin decir de qué sistema
   obligaría a adivinar el formato, y un asiento mal leído entra cuadrado y mal.

   Y lo que NO se mueve aquí: que el asiento de apertura CUADRE se revisa en el
   diario, y el enlace se queda. Si no cuadra, el problema está en lo que entregó
   el sistema anterior y hay que resolverlo antes de seguir — eso es un juicio.
   ========================================================================== */

function FormHistorico() {
  const { can } = useRbac()
  if (!can("edit")) return <SinPermiso />

  return (
    <Caja className="space-y-2.5">
      <p className="text-2xs leading-5 text-muted-foreground">
        {t("Dime de qué sistema viene y lo leo tal como lo exporta. No hay que convertir nada.")}
      </p>
      <CargaEnSitio
        plantilla="asientos-concar"
        motivo="Histórico de CONCAR cargado desde primeros pasos."
        invalidar={["journal", "trial-balance"]}
        explicacion={t("CONCAR (Perú): cada fila es una línea de asiento.")}
      />
      <CargaEnSitio
        plantilla="asientos-contpaqi"
        motivo="Histórico de CONTPAQi cargado desde primeros pasos."
        invalidar={["journal", "trial-balance"]}
        explicacion={t("CONTPAQi (México): cada fila es un movimiento de póliza.")}
      />
      <CargaEnSitio
        plantilla="asientos-odoo"
        motivo="Histórico de Odoo cargado desde primeros pasos."
        invalidar={["journal", "trial-balance"]}
        explicacion={t("Odoo, o cualquier sistema que exporte debe y haber.")}
      />
    </Caja>
  )
}
